// src/components/operationCenter/widgets/BarChartWidget.tsx
// Widget wrapper for DistributionChart from TokenScreener

import React, { useMemo, useState, useCallback, useRef, useEffect } from "react";
import type { WidgetProps, BarChartConfig } from "@/types/dashboard";
import { useTokensScreenerData } from "@/hooks/tokens/useTokensScreenerData";
import { useMetricsCatalog } from "@/hooks/useMetricsCatalog";
import { useCategoriesTree } from "@/hooks/dashboard/useCategoriesTree";
import { useTokenCategoryMap } from "@/hooks/tokens/compare/useCompareData";
import { filterTokenRows } from "@/utils/tokens";
import { TimeRangePicker } from "@/components/common";
import { MetricKeys } from "@/constants/metrics";
import DistributionChart from "@/components/tokenScreener/charts/DistributionChart";
import type { MetricInfo } from "@/types/metrics";
import type { CategoryTreeNode } from "@/types/dashboards/categories";
import type { TokenRow } from "@/types/movers";

type RangeKey = '1d' | '7d' | '30d' | '365d';

interface MetricOption {
  key: string;
  label: string;
}

const METRIC_OPTIONS: MetricOption[] = [
  { key: MetricKeys.PriceClose, label: "Price" },
  { key: MetricKeys.Volume, label: "Volume" },
  { key: MetricKeys.TVL, label: "TVL" },
  { key: MetricKeys.MarketCap, label: "Market Cap" },
  { key: MetricKeys.FDV, label: "FDV" },
  { key: MetricKeys.Fees, label: "Fees" },
];

const BarChartWidget: React.FC<WidgetProps<BarChartConfig>> = ({ 
  widget,
  onConfigChange 
}) => {
  const config = widget.config as BarChartConfig;
  const chartContainerRef = useRef<HTMLDivElement>(null);
  const [containerHeight, setContainerHeight] = useState<number>(300);

  // Measure container height for responsive chart
  useEffect(() => {
    const container = chartContainerRef.current;
    if (!container) return;

    const updateHeight = () => {
      const height = container.clientHeight;
      if (height > 0) setContainerHeight(height);
    };
    
    updateHeight();
    const observer = new ResizeObserver(updateHeight);
    observer.observe(container);
    
    return () => observer.disconnect();
  }, []);

  // State - initialized from config
  const [selectedMetric, setSelectedMetric] = useState(config.metric ?? MetricKeys.PriceClose);
  const [useChange, setUseChange] = useState(config.use_change ?? true);
  const [range, setRange] = useState<RangeKey>((config.time_range as RangeKey) ?? '7d');
  const initialCategory = useMemo(
    () => config.category ?? config.categories?.[0] ?? 'all',
    [config.category, config.categories]
  );
  const [category, setCategory] = useState<string>(initialCategory);
  const [logY, setLogY] = useState(false);
  const [logYAvailable, setLogYAvailable] = useState(true);

  // Data fetching
  const { data: rows } = useTokensScreenerData();
  const { roots: categoryRoots, maps } = useCategoriesTree();
  const { data: tokenCategoryMap } = useTokenCategoryMap();

  // Metrics catalog for labels
  const { data: catalog } = useMetricsCatalog();
  const catalogMap: Record<string, MetricInfo> = useMemo(() => {
    const map: Record<string, MetricInfo> = {};
    (catalog || []).forEach((m) => { 
      if ((m as MetricInfo)?.metric_key) map[m.metric_key] = m as MetricInfo; 
    });
    return map;
  }, [catalog]);

  // Filter rows by category
  const rowsFiltered = useMemo(() => {
    if (!Array.isArray(rows) || rows.length === 0) return [];
    return filterTokenRows(rows as TokenRow[], {
      search: '',
      mcMin: '',
      mcMax: '',
      category,
      maps,
      tokenCategoryMap: (tokenCategoryMap as unknown as Record<string, number[]>) || {},
      excludeStables: true,
      getTicker: (r) => String(r["tokenSymbol"] ?? ''),
      getName: (r) => String(r["tokenName"] ?? r["name"] ?? ''),
      getMarketCap: (r) => (r["market_cap"] as number | null) ?? (r[MetricKeys.MarketCap] as number | null) ?? null,
    });
  }, [rows, category, maps, tokenCategoryMap]);

  // Get flat list of categories
  const categoryOptions = useMemo(() => {
    if (!categoryRoots) return [];
    const getLeaves = (node: CategoryTreeNode): CategoryTreeNode[] => {
      if (node.is_leaf) return [node];
      return (node.children || []).flatMap(getLeaves);
    };
    return categoryRoots
      .filter((c) => c.category_code !== 'overview')
      .flatMap((root) => getLeaves(root));
  }, [categoryRoots]);

  // Check if metric supports change mode
  const supportsChange = useMemo(() => {
    if (!selectedMetric) return false;
    if (selectedMetric.startsWith('ratio:')) return false;
    const metric = catalogMap[selectedMetric];
    if (!metric) return true; // Default to true if not found
    return metric.agg_fn !== 'snapshot';
  }, [selectedMetric, catalogMap]);

  // Handlers
  const handleMetricChange = useCallback((e: React.ChangeEvent<HTMLSelectElement>) => {
    const metric = e.target.value;
    setSelectedMetric(metric);
    onConfigChange?.({ metric });
  }, [onConfigChange]);

  const handleRangeChange = useCallback((newRange: RangeKey) => {
    setRange(newRange);
    onConfigChange?.({ time_range: newRange });
  }, [onConfigChange]);

  const handleCategoryChange = useCallback((e: React.ChangeEvent<HTMLSelectElement>) => {
    const next = e.target.value;
    setCategory(next);
    onConfigChange?.({ category: next });
  }, [onConfigChange]);

  const handleChangeToggle = useCallback(() => {
    const newValue = !useChange;
    setUseChange(newValue);
    onConfigChange?.({ use_change: newValue });
  }, [useChange, onConfigChange]);

  const handleLogToggle = useCallback(() => {
    setLogY(prev => !prev);
  }, []);

  // Sync state with config when config changes externally
  // Sync state with config when config changes externally
  useEffect(() => {
    if (config.metric) {
      setSelectedMetric(config.metric);
    }
    if (config.time_range) {
      setRange(config.time_range as RangeKey);
    }
    if (config.use_change !== undefined) {
      setUseChange(config.use_change);
    }
    if (config.category || config.categories?.[0]) {
      setCategory(config.category ?? config.categories?.[0] ?? 'all');
    }
  }, [config.metric, config.time_range, config.use_change, config.category, config.categories]);

  const metricLabel = useMemo(() => {
    return METRIC_OPTIONS.find((m) => m.key === selectedMetric)?.label ?? selectedMetric;
  }, [selectedMetric]);

  const categoryLabel = useMemo(() => {
    if (category === "all") return "All Tokens";
    const match = categoryOptions.find((cat) => cat.category_code === category);
    return match?.category_name ?? category;
  }, [category, categoryOptions]);

  const chartTitle = useMemo(() => {
    const metricTitle = useChange ? `${metricLabel} %` : metricLabel;
    return `Bar Chart - ${metricTitle}, ${categoryLabel}`;
  }, [metricLabel, useChange, categoryLabel]);

  return (
    <div className="h-full flex flex-col overflow-hidden">
      <div className="flex items-center justify-between px-3 py-2 border-b border-slate-700/30">
        <span className="text-xs uppercase tracking-wide text-slate-300 font-medium">
          {chartTitle}
        </span>
      </div>
      {/* Controls */}
      <div className="flex-shrink-0 flex flex-wrap items-center gap-2 px-3 py-2 border-b border-gray-700/30">
        {/* Metric selector */}
        <select
          value={selectedMetric}
          onChange={handleMetricChange}
          className="select select-xs bg-gray-800/60 border-gray-700/50 focus:border-blue-500/50 text-slate-200 text-[11px] h-6 min-h-0"
        >
          {METRIC_OPTIONS.map(m => (
            <option key={m.key} value={m.key}>{m.label}</option>
          ))}
        </select>

        {/* Change toggle */}
        {supportsChange && (
          <button
            onClick={handleChangeToggle}
            className={`px-2 py-0.5 rounded text-[10px] font-medium transition-colors ${
              useChange 
                ? 'bg-blue-500/20 text-blue-300 border border-blue-500/30' 
                : 'bg-gray-800/50 text-slate-400 border border-gray-700/50 hover:text-slate-200'
            }`}
          >
            %Δ
          </button>
        )}

        {/* Log scale toggle */}
        {logYAvailable && (
          <button
            onClick={handleLogToggle}
            className={`px-2 py-0.5 rounded text-[10px] font-medium transition-colors ${
              logY 
                ? 'bg-blue-500/20 text-blue-300 border border-blue-500/30' 
                : 'bg-gray-800/50 text-slate-400 border border-gray-700/50 hover:text-slate-200'
            }`}
          >
            Log
          </button>
        )}

        {/* Category selector */}
        <select
          value={category}
          onChange={handleCategoryChange}
          className="select select-xs bg-gray-800/60 border-gray-700/50 focus:border-blue-500/50 text-slate-200 text-[11px] h-6 min-h-0 max-w-[100px]"
        >
          <option value="all">All</option>
          {categoryOptions.map((cat) => (
            <option key={cat.category_code} value={cat.category_code}>
              {cat.category_name}
            </option>
          ))}
        </select>

        {/* Time range */}
        <div className="ml-auto">
          <TimeRangePicker
            options={['1d', '7d', '30d', '365d'] as const}
            value={range}
            onChange={(v) => handleRangeChange(v as RangeKey)}
            theme="blue"
          />
        </div>
      </div>

      {/* Chart container */}
      <div ref={chartContainerRef} className="flex-1 min-h-0 overflow-hidden">
        <DistributionChart
          rows={rowsFiltered as Array<Record<string, unknown>>}
          range={range}
          xKey={selectedMetric}
          xUseChange={supportsChange ? useChange : false}
          logY={logY}
          setLogYAvailable={setLogYAvailable}
          search=""
          mcMin=""
          mcMax=""
          height={containerHeight}
        />
      </div>
    </div>
  );
};

export default BarChartWidget;
